import {
  Grid,
  Typography,
  Box,
  Container,
  TextField,
  Button,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  FormHelperText,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { MedicalInterviewType } from '../types/medicalInterview';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Amplify } from 'aws-amplify';
import config from '../../../amplifyconfiguration.json';
import { CloudUpload, DeleteSharp } from '@mui/icons-material';
import { uploadData } from '@aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import {
  jobTypesItems,
  purposeOfExaminationItems,
  workplaceTransferMethodItems,
  walkingHabitItems,
  aidsForWalkingItems,
  createdInHospitalsAndClinicsItems,
  diseasesRelatedToOrthopedicsItems,
  otherAllergiesItems,
  medicationsCurrentlyBeingTakenItems,
  aboutCigarettesItems,
  aboutAlcoholItems,
  primaryNursingCareRequirementAuthorizationItems
} from '../constants/MedicalInterview';
import { createObservation } from '@/graphql/mutations';
import { useActiveStep } from '../hooks/useActiveStep';
import { useLocation } from 'react-router';
import { CreateObservationMutation } from '@/API';
import { executeQuery } from '@/client/graphql';
import * as Sentry from '@sentry/react';
import { VITE_USER_BRANCH, FINGERPRINT_APPLICATION_ERROR, FINGERPRINT_SYSTEM_ERROR } from '@/lib/sentryClient';

Amplify.configure(config);

const schema: yup.ObjectSchema<MedicalInterviewType> = yup
  .object({
    height: yup
      .number()
      .positive('正の数を入力してください')
      .max(500, '500以下で設定してください。')
      .min(1, '1以上で設定してください。')
      .required('身長を入力してください')
      .typeError('数値を入力してください。'),
    weight: yup
      .number()
      .positive('正の数を入力してください')
      .max(1000, '1000以下で設定してください。')
      .min(1, '1以上で設定してください。')
      .required('体重を入力してください')
      .typeError('数値を入力してください。'),
    treatmentOfRecentInjuriesReceived: yup.boolean().required('選択してください。'),
    purposeOfExamination: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    purposeOfExaminationOtherReason: yup.string().when('purposeOfExamination', {
      is: (purposeOfExamination: string[]) => {
        return Array.isArray(purposeOfExamination) && purposeOfExamination?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    areaToBeInspected: yup.string().required('診察したい部位を入力してください。'),
    jobType: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    jobTypeOtherReason: yup.string().when('jobType', {
      is: (jobType: string[]) => {
        return Array.isArray(jobType) && jobType?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    workplaceTransferMethod: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    workplaceTransferMethodOtherReason: yup.string().when('workplaceTransferMethod', {
      is: (workplaceTransferMethod: string[]) => {
        return Array.isArray(workplaceTransferMethod) && workplaceTransferMethod?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    walkingHabit: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    aidsForWalking: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    createdInHospitalsAndClinics: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    insuranceCoverageForOrthoticDevices: yup.boolean().required('選択してください。'),
    unintentionalWeightLossOfMoreThan2kgInTheLast6Months: yup.boolean().required('選択してください。'),
    feelingTiredForNoReasonInTheLastTwoWeeks: yup.boolean().required('選択してください。'),
    lightExerciseForHealthPurposes: yup.boolean().required('選択してください。'),
    canYouOpenTheLidOfAPlasticBottle: yup.boolean().required('選択してください。'),
    diseasesRelatedToOrthopedics: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    sprainLocation: yup.string().when('diseasesRelatedToOrthopedics', {
      is: (diseasesRelatedToOrthopedics: string[]) => {
        return Array.isArray(diseasesRelatedToOrthopedics) && diseasesRelatedToOrthopedics?.includes('28');
      },
      then: (schema) => schema.required('捻挫した場所を入力してください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    fractureLocation: yup.string().when('diseasesRelatedToOrthopedics', {
      is: (diseasesRelatedToOrthopedics: string[]) => {
        return Array.isArray(diseasesRelatedToOrthopedics) && diseasesRelatedToOrthopedics?.includes('29');
      },
      then: (schema) => schema.required('骨折した場所を入力してください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    diseasesRelatedToOrthopedicsOtherReason: yup.string().when('diseasesRelatedToOrthopedics', {
      is: (diseasesRelatedToOrthopedics: string[]) => {
        return Array.isArray(diseasesRelatedToOrthopedics) && diseasesRelatedToOrthopedics?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    injuries1: yup.string().required('怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)を入力してください。'),
    injuries2: yup.string().required('怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)を入力してください。'),
    nameOfDrugAllergyMedication: yup.string().required('入力してください。'),
    nameOfMedicationIfAllergicToAnesthesia: yup.string().required('入力してください。'),
    otherAllergies: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    otherAllergiesOtherReason: yup.string().when('otherAllergies', {
      is: (otherAllergies: string[]) => {
        return Array.isArray(otherAllergies) && otherAllergies?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    medicationsCurrentlyBeingTaken: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .required('選択してください。'),
    medicationsCurrentlyBeingTakenOtherReason: yup.string().when('medicationsCurrentlyBeingTaken', {
      is: (medicationsCurrentlyBeingTaken: string[]) => {
        return Array.isArray(medicationsCurrentlyBeingTaken) && medicationsCurrentlyBeingTaken?.includes('99');
      },
      then: (schema) => schema.required('その他を選ばれた方はご記載ください。'),
      otherwise: (schema) => schema.notRequired()
    }),
    supplementsCurrentlyBeingTaken: yup.string().required('入力してください。'),
    aboutCigarettes: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    numberOfCigarettesSmokedDaily: yup
      .number()
      .positive('正の数を入力してください。')
      .max(1000, '1000以下で設定してください。')
      .min(0, '0以上で設定してください。')
      .required('入力してください。')
      .typeError('数値を入力してください。'),
    numberOfYearsSmoked: yup
      .number()
      .positive('正の数を入力してください。')
      .max(1000, '1000以下で設定してください。')
      .min(0, '0以上で設定してください。')
      .required('入力してください。')
      .typeError('数値を入力してください。'),
    aboutAlcohol: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    pregnancy: yup.boolean().required('選択してください。'),
    historyOfChildbirth: yup.boolean().required('選択してください。'),
    aboutFamilyDoctor: yup.string().required('入力してください。'),
    primaryNursingCareRequirementAuthorization: yup
      .array()
      .of(yup.string().required('選択してください。'))
      .min(1, '選択してください。')
      .required('選択してください。'),
    // s3のパスでもいれておく
    footSymptomsOfConcern: yup
      .mixed()
      .nullable()
      .optional()
      .test('fileSize', 'ファイルは5GB以下でなければなりません。', (value: any) => {
        if (!value) return true;
        if (value.length === 0) return true;

        for (let i = 0; i < value.length; i++) {
          // 5GBを超えるとpud出来ないはず。一旦時間優先
          if (value[i].size > 5 * 1024 * 1024 * 1024) {
            return false;
          }
        }

        // 全てのチェックを通過した場合は検証をパスする
        return true;
      })
  })
  .required();

export type MedicalInterviewForm = yup.InferType<typeof schema>;

export function MedicalInterview() {
  const { setActiveStep } = useActiveStep();
  const location = useLocation();
  const reservationId = location.state.reservationId;

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      height: 0,
      weight: 0,
      purposeOfExamination: [],
      purposeOfExaminationOtherReason: '',
      areaToBeInspected: '',
      jobType: [],
      jobTypeOtherReason: '',
      workplaceTransferMethod: [],
      workplaceTransferMethodOtherReason: '',
      walkingHabit: [],
      aidsForWalking: [],
      createdInHospitalsAndClinics: [],
      insuranceCoverageForOrthoticDevices: undefined,
      unintentionalWeightLossOfMoreThan2kgInTheLast6Months: undefined,
      feelingTiredForNoReasonInTheLastTwoWeeks: undefined,
      lightExerciseForHealthPurposes: undefined,
      canYouOpenTheLidOfAPlasticBottle: undefined,
      diseasesRelatedToOrthopedics: [],
      sprainLocation: '',
      fractureLocation: '',
      diseasesRelatedToOrthopedicsOtherReason: '',
      injuries1: '',
      injuries2: '',
      nameOfDrugAllergyMedication: '',
      nameOfMedicationIfAllergicToAnesthesia: '',
      otherAllergies: [],
      otherAllergiesOtherReason: '',
      medicationsCurrentlyBeingTaken: [],
      medicationsCurrentlyBeingTakenOtherReason: '',
      supplementsCurrentlyBeingTaken: '',
      aboutCigarettes: [],
      numberOfCigarettesSmokedDaily: 0,
      numberOfYearsSmoked: 0,
      aboutAlcohol: [],
      pregnancy: undefined,
      historyOfChildbirth: undefined,
      aboutFamilyDoctor: '',
      primaryNursingCareRequirementAuthorization: [],
      // s3のパスでもいれておく
      footSymptomsOfConcern: []
    }
  });

  const selectedJobType: string[] = watch('jobType');
  const selectedPurposeOfExamination: string[] = watch('purposeOfExamination');
  const selectedWorkplaceTransferMethod: string[] = watch('workplaceTransferMethod');
  const selectedWalkingHabit: string[] = watch('walkingHabit');
  const selectedAidsForWalking: string[] = watch('aidsForWalking');
  const selectedCreatedInHospitalsAndClinics: string[] = watch('createdInHospitalsAndClinics');
  const selectedDiseasesRelatedToOrthopedics: string[] = watch('diseasesRelatedToOrthopedics');
  const selectedOtherAllergies: string[] = watch('otherAllergies');
  const selectedMedicationsCurrentlyBeingTaken: string[] = watch('medicationsCurrentlyBeingTaken');
  const selectedAboutCigarettes: string[] = watch('aboutCigarettes');
  const selectedAboutAlcohol: string[] = watch('aboutAlcohol');
  const selectedPrimaryNursingCareRequirementAuthorization: string[] = watch(
    'primaryNursingCareRequirementAuthorization'
  );

  async function onSubmit(event: MedicalInterviewForm) {
    try {
      const session = await fetchAuthSession();
      const fileUploadPromises = event.footSymptomsOfConcern.map(async (file: any) => {
        const fileName = `${session.userSub}/${reservationId}/medical-interview/${file.name}`;
        const result = await uploadData({
          key: fileName,
          data: file,
          options: {
            accessLevel: 'private'
          }
        }).result;
        if (result.key === undefined) {
          throw new Error('ファイルのアップロードに失敗しました');
        }
        return result.key;
      });

      const createPatientParams = Object.assign(event, {
        patientId: session.userSub as string,
        footSymptomsOfConcern: [],
        reservationId
      });

      const uploadedFileNames = await Promise.all(fileUploadPromises);
      if (uploadedFileNames.length > 0) {
        createPatientParams.footSymptomsOfConcern = uploadedFileNames;
      }

      await executeQuery<CreateObservationMutation>(createObservation, { input: createPatientParams });

      // 共通部分
      const baseData = {
        resourceType: 'Observation',
        status: 'final'
      };
      // 選択された項目のラベルを取得
      const selectedLabels_AIBM004 = event.purposeOfExamination
        .map((id: string) => {
          const item = purposeOfExaminationItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.purposeOfExaminationOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM006 = event.jobType
        .map((id: string) => {
          const item = jobTypesItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.jobTypeOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM007 = event.workplaceTransferMethod
        .map((id: string) => {
          const item = workplaceTransferMethodItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.workplaceTransferMethodOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM008 = event.walkingHabit.map((id: string) => {
        const item = walkingHabitItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      const selectedLabels_AIBM009 = event.aidsForWalking.map((id: string) => {
        const item = aidsForWalkingItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      const selectedLabels_AIBM010 = event.createdInHospitalsAndClinics.map((id: string) => {
        const item = createdInHospitalsAndClinicsItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      const selectedLabels_AIBM016 = event.diseasesRelatedToOrthopedics
        .map((id: string) => {
          const item = diseasesRelatedToOrthopedicsItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.diseasesRelatedToOrthopedicsOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM023 = event.otherAllergies
        .map((id: string) => {
          const item = otherAllergiesItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.otherAllergiesOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM024 = event.medicationsCurrentlyBeingTaken
        .map((id: string) => {
          const item = medicationsCurrentlyBeingTakenItems.find((item) => item.id === id);
          if (item) {
            let label = item.label;
            if (id === '99') {
              label += `:${event.medicationsCurrentlyBeingTakenOtherReason}`;
            }
            return label;
          } else {
            return '';
          }
        })
        .filter(Boolean);
      const selectedLabels_AIBM026 = event.aboutCigarettes.map((id: string) => {
        const item = aboutCigarettesItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      const selectedLabels_AIBM028 = event.aboutAlcohol.map((id: string) => {
        const item = aboutAlcoholItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      const selectedLabels_AIBM032 = event.primaryNursingCareRequirementAuthorization.map((id: string) => {
        const item = primaryNursingCareRequirementAuthorizationItems.find((item) => item.id === id);
        return item ? item.label : '';
      });
      // はい、いいえ変換
      const treatmentOfRecentInjuriesReceivedDisplay = event.treatmentOfRecentInjuriesReceived ? 'はい' : 'いいえ';
      const insuranceCoverageForOrthoticDevicesDisplay = event.insuranceCoverageForOrthoticDevices ? 'はい' : 'いいえ';
      const unintentionalWeightLossOfMoreThan2kgInTheLast6MonthsDisplay =
        event.unintentionalWeightLossOfMoreThan2kgInTheLast6Months ? 'はい' : 'いいえ';
      const feelingTiredForNoReasonInTheLastTwoWeeksDisplay = event.feelingTiredForNoReasonInTheLastTwoWeeks
        ? 'はい'
        : 'いいえ';
      const lightExerciseForHealthPurposesDisplay = event.lightExerciseForHealthPurposes ? 'はい' : 'いいえ';
      const canYouOpenTheLidOfAPlasticBottleDisplay = event.canYouOpenTheLidOfAPlasticBottle ? 'はい' : 'いいえ';
      const pregnancyDisplay = event.pregnancy ? 'はい' : 'いいえ';
      const historyOfChildbirthDisplay = event.historyOfChildbirth ? 'はい' : 'いいえ';

      // 質問内容とコード一覧
      const displayValues = {
        AIBM001: '身長',
        AIBM002: '体重',
        AIBM003: '今回の受診目的は、最近受けた怪我の治療ですか?',
        AIBM004: '受診の目的、解決したい問題',
        AIBM005: '気になる箇所',
        AIBM006: '仕事の種類',
        AIBM007: '職場までの移動方法',
        AIBM008: '歩行習慣',
        AIBM009: '歩行に必要な補助具',
        AIBM010: '以下のものを病院やクリニックで作成したことがありますか?',
        AIBM011: '作成時、保険を使用しましたか?',
        AIBM012: '最近6か月で意図しない2kg以上の体重減少がありましたか?',
        AIBM013: 'ここ2週間で理由なく疲れたような感じがしますか?',
        AIBM014: '健康目的に適度な運動やスポーツ、もしくは軽い運動をしていますか?',
        AIBM015: 'ペットボトルの蓋をご自身で開けられますか?',
        AIBM016: 'これまでにかかった整形外科に関する病気について教えてください',
        AIBM017: '捻挫をされた方は捻挫した場所を教えてください。',
        AIBM018: '骨折をされた方は骨折した場所を教えてください。',
        AIBM019:
          '怪我1:今までに下肢の怪我(捻挫、靭帯・半月板損傷、骨折など)をしたことがありますか?怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)',
        AIBM020:
          '怪我2:今までに下肢の怪我(捻挫、靭帯・半月板損傷、骨折など)をしたことがその他にもありますか?怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)',
        AIBM021: '薬物アレルギーがある場合は薬の名前を教えてください',
        AIBM022: '麻酔アレルギーがある場合は薬の名前を教えてください',
        AIBM023: 'その他のアレルギーはありますか?',
        AIBM024: '現在のまれている薬を教えてください',
        AIBM025: '現在のまれているサプリメントがあれば教えてください',
        AIBM026: 'タバコについて※',
        AIBM027: '吸っている・やめた方はタバコを吸う量と年数を教えてください',
        AIBM028: 'お酒について',
        AIBM029: '女性の方に伺います 現在妊娠中もしくは妊娠の可能性がありますか?',
        AIBM030: '出産歴について',
        AIBM031: 'かかりつけ医や訪問医がいましたら教えてください',
        AIBM032: '要介護認定をお持ちの方は教えてください',
        AIBM033: '気になる足の症状がありましたら、画像や動画を送信してください。'
      };

      // フォームから取得したデータをHealthLakeのJSON形式に加工
      const jsonData_list = [
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM001',
                display: displayValues['AIBM001']
              }
            ]
          },
          valueString: event.height.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM002',
                display: displayValues['AIBM002']
              }
            ]
          },
          valueString: event.weight.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM003',
                display: displayValues['AIBM003']
              }
            ]
          },
          valueString: treatmentOfRecentInjuriesReceivedDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM004',
                display: displayValues['AIBM004']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM004].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM005',
                display: displayValues['AIBM005']
              }
            ]
          },
          valueString: event.areaToBeInspected.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM006',
                display: displayValues['AIBM006']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM006].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM007',
                display: displayValues['AIBM007']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM007].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM008',
                display: displayValues['AIBM008']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM008].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM009',
                display: displayValues['AIBM009']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM009].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM010',
                display: displayValues['AIBM010']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM010].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM011',
                display: displayValues['AIBM011']
              }
            ]
          },
          valueString: insuranceCoverageForOrthoticDevicesDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM012',
                display: displayValues['AIBM012']
              }
            ]
          },
          valueString: unintentionalWeightLossOfMoreThan2kgInTheLast6MonthsDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM013',
                display: displayValues['AIBM013']
              }
            ]
          },
          valueString: feelingTiredForNoReasonInTheLastTwoWeeksDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM014',
                display: displayValues['AIBM014']
              }
            ]
          },
          valueString: lightExerciseForHealthPurposesDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM015',
                display: displayValues['AIBM015']
              }
            ]
          },
          valueString: canYouOpenTheLidOfAPlasticBottleDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM016',
                display: displayValues['AIBM016']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM016].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM017',
                display: displayValues['AIBM017']
              }
            ]
          },
          valueString: event.sprainLocation
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM018',
                display: displayValues['AIBM018']
              }
            ]
          },
          valueString: event.fractureLocation
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM019',
                display: displayValues['AIBM019']
              }
            ]
          },
          valueString: event.injuries1.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM020',
                display: displayValues['AIBM020']
              }
            ]
          },
          valueString: event.injuries2.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM021',
                display: displayValues['AIBM021']
              }
            ]
          },
          valueString: event.nameOfDrugAllergyMedication.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM022',
                display: displayValues['AIBM022']
              }
            ]
          },
          valueString: event.nameOfMedicationIfAllergicToAnesthesia.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM023',
                display: displayValues['AIBM023']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM023].toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM024',
                display: displayValues['AIBM024']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM024].toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM025',
                display: displayValues['AIBM025']
              }
            ]
          },
          valueString: event.supplementsCurrentlyBeingTaken.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM026',
                display: displayValues['AIBM026']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM026].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM027',
                display: displayValues['AIBM027']
              }
            ]
          },
          valueString: `${event.numberOfCigarettesSmokedDaily}本/日,${event.numberOfYearsSmoked}年`
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM028',
                display: displayValues['AIBM028']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM028].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM029',
                display: displayValues['AIBM029']
              }
            ]
          },
          valueString: pregnancyDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM030',
                display: displayValues['AIBM030']
              }
            ]
          },
          valueString: historyOfChildbirthDisplay.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM031',
                display: displayValues['AIBM031']
              }
            ]
          },
          valueString: event.aboutFamilyDoctor.toString()
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM032',
                display: displayValues['AIBM032']
              }
            ]
          },
          valueString: [...selectedLabels_AIBM032].join(',')
        },
        {
          ...baseData,
          code: {
            coding: [
              {
                code: 'AIBM033',
                display: displayValues['AIBM033']
              }
            ]
          },
          valueString: event.footSymptomsOfConcern.join(',')
        }
      ];

      console.log(jsonData_list);
      // 本番用RESTAPIにURL変更が必要
      // axios.post('https://h6bzqh451k.execute-api.ap-northeast-1.amazonaws.com/develop/post', jsonData_list);

      axios
        .post('https://0xyad3zj2f.execute-api.ap-northeast-1.amazonaws.com/test/', jsonData_list)
        .then()
        .catch((error) => {
          // 4xxのエラーはアプリケーションエラーとして管理する
          if (error?.status && error?.status > 399 && error?.status < 500) {
            Sentry.captureException(error, {
              fingerprint: [VITE_USER_BRANCH, error.config.url, FINGERPRINT_APPLICATION_ERROR],
              extra: {
                isCustomEvent: true,
                HTTP: {
                  url: error.config.url,
                  statusCode: error.response?.status,
                  request: JSON.stringify(error.request)
                }
              }
            });
            return;
          }

          // 5xxエラー関連
          Sentry.captureException(error, {
            fingerprint: [VITE_USER_BRANCH, error.config.url, FINGERPRINT_SYSTEM_ERROR],
            extra: {
              isCustomEvent: true,
              HTTP: {
                url: error.config.url,
                statusCode: error.response?.status,
                request: JSON.stringify(error.request)
              }
            }
          });
          return;
        });
      setActiveStep('Completed');
    } catch (e) {
      // TODO: fetchAuthSession
      console.error(e);
    }
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ p: 2, 'overflow-anchor': 'none' }}>
        <Typography variant="h5" gutterBottom>
          事前問診
        </Typography>
        <Typography style={{ marginLeft: '8px', marginBottom: '8px' }}>
          以下の問診のご回答をお願いいたします。
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Typography variant="h6" gutterBottom>
                基本情報
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="height"
                control={control}
                rules={{ required: '身長を入力してください', pattern: /^\d+$/ }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="身長"
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">cm</InputAdornment>
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="weight"
                control={control}
                rules={{ required: '体重を入力してください', pattern: /^\d+$/ }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    label="体重"
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">kg</InputAdornment>
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={12}>
              <Typography variant="h6" gutterBottom>
                診療に関わる情報
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>今回の受診目的は、最近受けた怪我の治療ですか?</Typography>
              <Controller
                name="treatmentOfRecentInjuriesReceived"
                control={control}
                render={({ field }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.treatmentOfRecentInjuriesReceived && (
                      <FormHelperText error>{errors.treatmentOfRecentInjuriesReceived.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>受診の目的、解決したい問題はなんですか?</Typography>
              {purposeOfExaminationItems.map((purposeOfExaminationItem) => (
                <Controller
                  key={purposeOfExaminationItem.id}
                  name="purposeOfExamination"
                  control={control}
                  render={({ field }) => (
                    <>
                      <Button
                        variant={
                          selectedPurposeOfExamination?.includes(purposeOfExaminationItem.id) ? 'contained' : 'outlined'
                        }
                        color="primary"
                        onClick={() => {
                          const newValue = selectedPurposeOfExamination.includes(purposeOfExaminationItem.id)
                            ? selectedPurposeOfExamination.filter((id) => id !== purposeOfExaminationItem.id)
                            : [...selectedPurposeOfExamination, purposeOfExaminationItem.id];
                          field.onChange(newValue);
                        }}
                        sx={{ margin: 0.5 }}
                      >
                        {purposeOfExaminationItem.label}
                      </Button>
                    </>
                  )}
                />
              ))}
              {errors.purposeOfExamination && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.purposeOfExamination.message}
                </FormHelperText>
              )}
              {selectedPurposeOfExamination?.includes('99') && (
                <Controller
                  name="purposeOfExaminationOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.purposeOfExaminationOtherReason}
                      helperText={
                        errors.purposeOfExaminationOtherReason ? errors.purposeOfExaminationOtherReason.message : ''
                      }
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>気になる箇所</Typography>
              <Controller
                name="areaToBeInspected"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    error={!!errors.areaToBeInspected}
                    helperText={errors.areaToBeInspected ? errors.areaToBeInspected.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>仕事の種類</Typography>
              {jobTypesItems.map((jobTypesItem) => (
                <Controller
                  key={jobTypesItem.id}
                  name="jobType"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedJobType.includes(jobTypesItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedJobType.includes(jobTypesItem.id)
                          ? selectedJobType.filter((id) => id !== jobTypesItem.id)
                          : [...selectedJobType, jobTypesItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {jobTypesItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.jobType && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.jobType.message}
                </FormHelperText>
              )}
              {selectedJobType.includes('99') && (
                <Controller
                  name="jobTypeOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.jobTypeOtherReason}
                      helperText={errors.jobTypeOtherReason ? errors.jobTypeOtherReason.message : ''}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>職場までの移動方法</Typography>
              {workplaceTransferMethodItems.map((workplaceTransferMethodItem) => (
                <Controller
                  key={workplaceTransferMethodItem.id}
                  name="workplaceTransferMethod"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={
                        selectedWorkplaceTransferMethod.includes(workplaceTransferMethodItem.id)
                          ? 'contained'
                          : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        const newValue = selectedWorkplaceTransferMethod.includes(workplaceTransferMethodItem.id)
                          ? selectedWorkplaceTransferMethod.filter((id) => id !== workplaceTransferMethodItem.id)
                          : [...selectedWorkplaceTransferMethod, workplaceTransferMethodItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {workplaceTransferMethodItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.workplaceTransferMethod && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.workplaceTransferMethod.message}
                </FormHelperText>
              )}
              {selectedWorkplaceTransferMethod.includes('99') && (
                <Controller
                  name="workplaceTransferMethodOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.workplaceTransferMethodOtherReason}
                      helperText={
                        errors.workplaceTransferMethodOtherReason
                          ? errors.workplaceTransferMethodOtherReason.message
                          : ''
                      }
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>歩行習慣</Typography>
              {walkingHabitItems.map((walkingHabitItem) => (
                <Controller
                  key={walkingHabitItem.id}
                  name="walkingHabit"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedWalkingHabit.includes(walkingHabitItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedWalkingHabit.includes(walkingHabitItem.id)
                          ? selectedWalkingHabit.filter((id) => id !== walkingHabitItem.id)
                          : [...selectedWalkingHabit, walkingHabitItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {walkingHabitItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.walkingHabit && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.walkingHabit.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>歩行に必要な補助具</Typography>
              {aidsForWalkingItems.map((aidsForWalkingItem) => (
                <Controller
                  key={aidsForWalkingItem.id}
                  name="aidsForWalking"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedAidsForWalking.includes(aidsForWalkingItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedAidsForWalking.includes(aidsForWalkingItem.id)
                          ? selectedAidsForWalking.filter((id) => id !== aidsForWalkingItem.id)
                          : [...selectedAidsForWalking, aidsForWalkingItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {aidsForWalkingItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.aidsForWalking && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.aidsForWalking.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>以下のものを病院やクリニックで作成したことがありますか?</Typography>
              {createdInHospitalsAndClinicsItems.map((createdInHospitalsAndClinicsItem) => (
                <Controller
                  key={createdInHospitalsAndClinicsItem.id}
                  name="createdInHospitalsAndClinics"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={
                        selectedCreatedInHospitalsAndClinics.includes(createdInHospitalsAndClinicsItem.id)
                          ? 'contained'
                          : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        const newValue = selectedCreatedInHospitalsAndClinics.includes(
                          createdInHospitalsAndClinicsItem.id
                        )
                          ? selectedCreatedInHospitalsAndClinics.filter(
                              (id) => id !== createdInHospitalsAndClinicsItem.id
                            )
                          : [...selectedCreatedInHospitalsAndClinics, createdInHospitalsAndClinicsItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {createdInHospitalsAndClinicsItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.createdInHospitalsAndClinics && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.createdInHospitalsAndClinics.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>上記で作成したことがある方のみお聞きします。作成時、保険を使用しましたか?</Typography>
              <Controller
                name="insuranceCoverageForOrthoticDevices"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.insuranceCoverageForOrthoticDevices && (
                      <FormHelperText error>{errors.insuranceCoverageForOrthoticDevices.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>最近6か月で意図しない2kg以上の体重減少がありましたか?</Typography>
              <Controller
                name="unintentionalWeightLossOfMoreThan2kgInTheLast6Months"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.unintentionalWeightLossOfMoreThan2kgInTheLast6Months && (
                      <FormHelperText error>
                        {errors.unintentionalWeightLossOfMoreThan2kgInTheLast6Months.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>ここ2週間で理由なく疲れたような感じがしますか?</Typography>
              <Controller
                name="feelingTiredForNoReasonInTheLastTwoWeeks"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.feelingTiredForNoReasonInTheLastTwoWeeks && (
                      <FormHelperText error>{errors.feelingTiredForNoReasonInTheLastTwoWeeks.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>健康目的に適度な運動やスポーツ、もしくは軽い運動をしていますか?</Typography>
              <Controller
                name="lightExerciseForHealthPurposes"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.lightExerciseForHealthPurposes && (
                      <FormHelperText error>{errors.lightExerciseForHealthPurposes.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>ペットボトルの蓋をご自身で開けられますか?</Typography>
              <Controller
                name="canYouOpenTheLidOfAPlasticBottle"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.canYouOpenTheLidOfAPlasticBottle && (
                      <FormHelperText error>{errors.canYouOpenTheLidOfAPlasticBottle.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>これまでにかかった整形外科に関する病気について教えてください</Typography>
              {diseasesRelatedToOrthopedicsItems.map((diseasesRelatedToOrthopedicsItem) => (
                <Controller
                  key={diseasesRelatedToOrthopedicsItem.id}
                  name="diseasesRelatedToOrthopedics"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={
                        selectedDiseasesRelatedToOrthopedics.includes(diseasesRelatedToOrthopedicsItem.id)
                          ? 'contained'
                          : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        const newValue = selectedDiseasesRelatedToOrthopedics.includes(
                          diseasesRelatedToOrthopedicsItem.id
                        )
                          ? selectedDiseasesRelatedToOrthopedics.filter(
                              (id) => id !== diseasesRelatedToOrthopedicsItem.id
                            )
                          : [...selectedDiseasesRelatedToOrthopedics, diseasesRelatedToOrthopedicsItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {diseasesRelatedToOrthopedicsItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.diseasesRelatedToOrthopedics && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.diseasesRelatedToOrthopedics.message}
                </FormHelperText>
              )}
              {selectedDiseasesRelatedToOrthopedics.includes('28') && (
                <Controller
                  name="sprainLocation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="捻挫をされた方は捻挫した場所を教えてください。"
                      variant="outlined"
                      fullWidth
                      error={!!errors.sprainLocation}
                      helperText={errors.sprainLocation ? errors.sprainLocation.message : ''}
                    />
                  )}
                />
              )}
              {selectedDiseasesRelatedToOrthopedics.includes('29') && (
                <Controller
                  name="fractureLocation"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="骨折をされた方は骨折した場所を教えてください。"
                      variant="outlined"
                      fullWidth
                      error={!!errors.fractureLocation}
                      helperText={errors.fractureLocation ? errors.fractureLocation.message : ''}
                    />
                  )}
                />
              )}
              {selectedDiseasesRelatedToOrthopedics.includes('99') && (
                <Controller
                  name="diseasesRelatedToOrthopedicsOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.diseasesRelatedToOrthopedicsOtherReason}
                      helperText={
                        errors.diseasesRelatedToOrthopedicsOtherReason
                          ? errors.diseasesRelatedToOrthopedicsOtherReason.message
                          : ''
                      }
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>怪我1:今までに下肢の怪我(捻挫、靭帯・半月板損傷、骨折など)をしたことがありますか?</Typography>
              <Controller
                name="injuries1"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label="怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)"
                    fullWidth
                    error={!!errors.injuries1}
                    helperText={errors.injuries1 ? errors.injuries1.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>
                怪我2:今までに下肢の怪我(捻挫、靭帯・半月板損傷、骨折など)をしたことがその他にもありますか?
              </Typography>
              <Controller
                name="injuries2"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    label="怪我した場所、いつ頃か(何か月前や何年前)、治療方法(手術等)"
                    fullWidth
                    error={!!errors.injuries2}
                    helperText={errors.injuries2 ? errors.injuries2.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>薬物アレルギーがある場合は薬の名前を教えてください</Typography>
              <Controller
                name="nameOfDrugAllergyMedication"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    error={!!errors.nameOfDrugAllergyMedication}
                    helperText={errors.nameOfDrugAllergyMedication ? errors.nameOfDrugAllergyMedication.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>麻酔アレルギーがある場合は薬の名前を教えてください</Typography>
              <Controller
                name="nameOfMedicationIfAllergicToAnesthesia"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    error={!!errors.nameOfMedicationIfAllergicToAnesthesia}
                    helperText={
                      errors.nameOfMedicationIfAllergicToAnesthesia
                        ? errors.nameOfMedicationIfAllergicToAnesthesia.message
                        : ''
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>その他のアレルギーはありますか?</Typography>
              {otherAllergiesItems.map((otherAllergiesItem) => (
                <Controller
                  key={otherAllergiesItem.id}
                  name="otherAllergies"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedOtherAllergies.includes(otherAllergiesItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedOtherAllergies.includes(otherAllergiesItem.id)
                          ? selectedOtherAllergies.filter((id) => id !== otherAllergiesItem.id)
                          : [...selectedOtherAllergies, otherAllergiesItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {otherAllergiesItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.otherAllergies && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.otherAllergies.message}
                </FormHelperText>
              )}
              {selectedOtherAllergies.includes('99') && (
                <Controller
                  name="otherAllergiesOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.otherAllergiesOtherReason}
                      helperText={errors.otherAllergiesOtherReason ? errors.otherAllergiesOtherReason.message : ''}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>現在のまれている薬を教えてください</Typography>
              {medicationsCurrentlyBeingTakenItems.map((medicationsCurrentlyBeingTakenItem) => (
                <Controller
                  key={medicationsCurrentlyBeingTakenItem.id}
                  name="medicationsCurrentlyBeingTaken"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={
                        selectedMedicationsCurrentlyBeingTaken.includes(medicationsCurrentlyBeingTakenItem.id)
                          ? 'contained'
                          : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        const newValue = selectedMedicationsCurrentlyBeingTaken.includes(
                          medicationsCurrentlyBeingTakenItem.id
                        )
                          ? selectedMedicationsCurrentlyBeingTaken.filter(
                              (id) => id !== medicationsCurrentlyBeingTakenItem.id
                            )
                          : [...selectedMedicationsCurrentlyBeingTaken, medicationsCurrentlyBeingTakenItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {medicationsCurrentlyBeingTakenItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.medicationsCurrentlyBeingTaken && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.medicationsCurrentlyBeingTaken.message}
                </FormHelperText>
              )}
              {selectedMedicationsCurrentlyBeingTaken.includes('99') && (
                <Controller
                  name="medicationsCurrentlyBeingTakenOtherReason"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="その他の理由"
                      variant="outlined"
                      fullWidth
                      error={!!errors.medicationsCurrentlyBeingTakenOtherReason}
                      helperText={
                        errors.medicationsCurrentlyBeingTakenOtherReason
                          ? errors.medicationsCurrentlyBeingTakenOtherReason.message
                          : ''
                      }
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>現在のまれているサプリメントがあれば教えてください</Typography>
              <Controller
                name="supplementsCurrentlyBeingTaken"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    error={!!errors.supplementsCurrentlyBeingTaken}
                    helperText={
                      errors.supplementsCurrentlyBeingTaken ? errors.supplementsCurrentlyBeingTaken.message : ''
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>タバコについて</Typography>
              {aboutCigarettesItems.map((aboutCigarettesItem) => (
                <Controller
                  key={aboutCigarettesItem.id}
                  name="aboutCigarettes"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedAboutCigarettes.includes(aboutCigarettesItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedAboutCigarettes.includes(aboutCigarettesItem.id)
                          ? selectedAboutCigarettes.filter((id) => id !== aboutCigarettesItem.id)
                          : [...selectedAboutCigarettes, aboutCigarettesItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {aboutCigarettesItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.aboutCigarettes && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.aboutCigarettes.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>吸っている・やめた方はタバコを吸う量と年数を教えてください</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="numberOfCigarettesSmokedDaily"
                control={control}
                rules={{ required: '一日に吸う本数を入力してください', pattern: /^\d+$/ }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">本/日</InputAdornment>
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="numberOfYearsSmoked"
                control={control}
                rules={{ required: '喫煙年数を入力してください', pattern: /^\d+$/ }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">年</InputAdornment>
                    }}
                    error={!!error}
                    helperText={error ? error.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>お酒について</Typography>
              {aboutAlcoholItems.map((aboutAlcoholItem) => (
                <Controller
                  key={aboutAlcoholItem.id}
                  name="aboutAlcohol"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={selectedAboutAlcohol.includes(aboutAlcoholItem.id) ? 'contained' : 'outlined'}
                      color="primary"
                      onClick={() => {
                        const newValue = selectedAboutAlcohol.includes(aboutAlcoholItem.id)
                          ? selectedAboutAlcohol.filter((id) => id !== aboutAlcoholItem.id)
                          : [...selectedAboutAlcohol, aboutAlcoholItem.id];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {aboutAlcoholItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.aboutAlcohol && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.aboutAlcohol.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>女性の方に伺います。現在妊娠中もしくは妊娠の可能性がありますか?</Typography>
              <Controller
                name="pregnancy"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.pregnancy && <FormHelperText error>{errors.pregnancy.message}</FormHelperText>}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>出産歴について</Typography>
              <Controller
                name="historyOfChildbirth"
                control={control}
                render={({ field, formState: { errors } }) => (
                  <>
                    <ToggleButtonGroup
                      exclusive
                      {...field}
                      onChange={(_, value) => field.onChange(value)}
                      value={field.value}
                      fullWidth
                      color="primary"
                    >
                      <ToggleButton value={true}>はい</ToggleButton>
                      <ToggleButton value={false}>いいえ</ToggleButton>
                    </ToggleButtonGroup>
                    {errors.historyOfChildbirth && (
                      <FormHelperText error>{errors.historyOfChildbirth.message}</FormHelperText>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>かかりつけ医や訪問医がいましたら教えてください</Typography>
              <Controller
                name="aboutFamilyDoctor"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    variant="outlined"
                    fullWidth
                    error={!!errors.aboutFamilyDoctor}
                    helperText={errors.aboutFamilyDoctor ? errors.aboutFamilyDoctor.message : ''}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>要介護認定をお持ちの方は教えてください</Typography>
              {primaryNursingCareRequirementAuthorizationItems.map((primaryNursingCareRequirementAuthorizationItem) => (
                <Controller
                  key={primaryNursingCareRequirementAuthorizationItem.id}
                  name="primaryNursingCareRequirementAuthorization"
                  control={control}
                  render={({ field }) => (
                    <Button
                      variant={
                        selectedPrimaryNursingCareRequirementAuthorization.includes(
                          primaryNursingCareRequirementAuthorizationItem.id
                        )
                          ? 'contained'
                          : 'outlined'
                      }
                      color="primary"
                      onClick={() => {
                        const newValue = selectedPrimaryNursingCareRequirementAuthorization.includes(
                          primaryNursingCareRequirementAuthorizationItem.id
                        )
                          ? selectedPrimaryNursingCareRequirementAuthorization.filter(
                              (id) => id !== primaryNursingCareRequirementAuthorizationItem.id
                            )
                          : [
                              ...selectedPrimaryNursingCareRequirementAuthorization,
                              primaryNursingCareRequirementAuthorizationItem.id
                            ];
                        field.onChange(newValue);
                      }}
                      sx={{ margin: 0.5 }}
                    >
                      {primaryNursingCareRequirementAuthorizationItem.label}
                    </Button>
                  )}
                />
              ))}
              {errors.primaryNursingCareRequirementAuthorization && (
                <FormHelperText error sx={{ display: 'block' }}>
                  {errors.primaryNursingCareRequirementAuthorization.message}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              <Typography>気になる足の症状がありましたら、画像や動画を送信してください。</Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Controller
                  name="footSymptomsOfConcern"
                  control={control}
                  defaultValue={[]}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Grid item xs={12} sm={12}>
                      <Box
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const filesArray = Array.from(e.dataTransfer.files);
                          onChange([...value, ...filesArray]);
                        }}
                        sx={{
                          border: '2px dashed #1976d2',
                          padding: '20px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          backgroundColor: '#fafafa',
                          marginBottom: '20px',
                          textAlign: 'center',
                          transition: 'background-color 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          '&:hover': {
                            backgroundColor: '#e3f2fd',
                            opacity: 1
                          }
                        }}
                      >
                        ファイルをドラッグ＆ドロップ、または
                        <Button variant="outlined" component="label" sx={{ ml: 1 }} startIcon={<CloudUpload />}>
                          ファイルを選択
                          <input
                            type="file"
                            multiple
                            hidden
                            onChange={(e) => {
                              if (e.target.files) {
                                const filesArray = Array.from(e.target.files);
                                onChange([...value, ...filesArray]);
                              }
                            }}
                          />
                        </Button>
                      </Box>

                      {error && <Typography color="error">{error.message}</Typography>}
                      <List>
                        {value.map((file: any, index: any) => (
                          <ListItem
                            key={index}
                            secondaryAction={
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => {
                                  const newValue = [...value];
                                  // 明示的に index を number 型として扱う
                                  if (typeof index === 'number') {
                                    newValue.splice(index, 1);
                                    onChange(newValue);
                                  }
                                }}
                              >
                                <DeleteSharp />
                              </IconButton>
                            }
                          >
                            <ListItemText primary={file.name} />
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  )}
                />
              </Stack>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setActiveStep('ConfirmationTransactionAgreement')}
              sx={{ mt: 3, mb: 2, margin: 2 }}
            >
              戻る
            </Button>
            <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2, margin: 2 }}>
              登録
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  );
}
